import { apiRequest } from '../api/client';
import { fetchAllPages } from '../api/fetch-all-pages';

export const MENU_STATUS_FILTERS = ['ACTIVE', 'INACTIVE', 'ARCHIVED'] as const;
export type MenuStatusFilter = (typeof MENU_STATUS_FILTERS)[number];

export const MENU_STATUS_FILTER_LABELS: Record<MenuStatusFilter, string> = {
  ACTIVE: 'Activos',
  INACTIVE: 'Inactivos',
  ARCHIVED: 'Archivados',
};

export interface ServiceType {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
}

export interface DietaryTag {
  id: string;
  code: string;
  name: string;
  color: string | null;
  icon: string | null;
  displayOrder: number;
  isActive: boolean;
}

export interface Allergen {
  id: string;
  code: string;
  name: string;
  icon: string | null;
  displayOrder: number;
  isActive: boolean;
}

/** Matches `apps/api` `GET /service-types` (`list-service-types.use-case.ts`), ordered by `displayOrder`. */
export function listServiceTypes(): Promise<ServiceType[]> {
  return apiRequest<ServiceType[]>('/service-types');
}

/** Matches `apps/api` `GET /dietary-tags` (`list-dietary-tags.use-case.ts`), ordered by `displayOrder`. */
export function listDietaryTags(): Promise<DietaryTag[]> {
  return apiRequest<DietaryTag[]>('/dietary-tags');
}

/** Matches `apps/api` `GET /allergens` (`list-allergens.use-case.ts`), ordered by `displayOrder`. */
export function listAllergens(): Promise<Allergen[]> {
  return apiRequest<Allergen[]>('/allergens');
}

/** Matches `apps/api` `StoredMenuPosition` (`menu-position.repository.ts`). */
export interface MenuPosition {
  id: string;
  code: string;
  name: string;
  serviceTypeId: string;
  displayOrder: number;
  isActive: boolean;
}

/** Matches `apps/api` `createMenuPositionSchema` (`menu-position.dto.ts`). */
export interface MenuPositionFormInput {
  code: string;
  name: string;
  serviceTypeId: string;
  displayOrder: number;
}

/** Matches `apps/api` `updateMenuPositionSchema`: `code` is immutable, no field to change it. */
export interface MenuPositionUpdateInput {
  name?: string;
  serviceTypeId?: string;
  displayOrder?: number;
  isActive?: boolean;
}

/** Matches `apps/api` `GET /menu-positions` (`list-menu-positions.use-case.ts`), ordered by `displayOrder`. */
export function listMenuPositions(): Promise<MenuPosition[]> {
  return apiRequest<MenuPosition[]>('/menu-positions');
}

/** Matches `apps/api` `POST /menu-positions` (`menu-positions.controller.ts`). */
export function createMenuPosition(input: MenuPositionFormInput): Promise<MenuPosition> {
  return apiRequest<MenuPosition>('/menu-positions', { method: 'POST', body: JSON.stringify(input) });
}

/** Matches `apps/api` `PATCH /menu-positions/:id` (`menu-positions.controller.ts`). */
export function updateMenuPosition(
  id: string,
  input: MenuPositionUpdateInput,
): Promise<MenuPosition> {
  return apiRequest<MenuPosition>(`/menu-positions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `createServiceTypeSchema` (`service-type.dto.ts`). */
export interface ServiceTypeFormInput {
  code: string;
  name: string;
  displayOrder: number;
}

/** Matches `apps/api` `updateServiceTypeSchema`: `code` is immutable, no field to change it. */
export interface ServiceTypeUpdateInput {
  name?: string;
  displayOrder?: number;
  isActive?: boolean;
}

/** Matches `apps/api` `POST /service-types` (`service-types.controller.ts`). */
export function createServiceType(input: ServiceTypeFormInput): Promise<ServiceType> {
  return apiRequest<ServiceType>('/service-types', { method: 'POST', body: JSON.stringify(input) });
}

/** Matches `apps/api` `PATCH /service-types/:id` (`service-types.controller.ts`). */
export function updateServiceType(id: string, input: ServiceTypeUpdateInput): Promise<ServiceType> {
  return apiRequest<ServiceType>(`/service-types/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `createDietaryTagSchema` (`dietary-tag.dto.ts`). */
export interface DietaryTagFormInput {
  code: string;
  name: string;
  color?: string;
  icon?: string;
  displayOrder: number;
}

/** Matches `apps/api` `updateDietaryTagSchema`: `code` is immutable, no field to change it. */
export interface DietaryTagUpdateInput {
  name?: string;
  color?: string | null;
  icon?: string | null;
  displayOrder?: number;
  isActive?: boolean;
}

/** Matches `apps/api` `POST /dietary-tags` (`dietary-tags.controller.ts`). */
export function createDietaryTag(input: DietaryTagFormInput): Promise<DietaryTag> {
  return apiRequest<DietaryTag>('/dietary-tags', { method: 'POST', body: JSON.stringify(input) });
}

/** Matches `apps/api` `PATCH /dietary-tags/:id` (`dietary-tags.controller.ts`). */
export function updateDietaryTag(id: string, input: DietaryTagUpdateInput): Promise<DietaryTag> {
  return apiRequest<DietaryTag>(`/dietary-tags/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `createAllergenSchema` (`allergen.dto.ts`). */
export interface AllergenFormInput {
  code: string;
  name: string;
  icon?: string;
  displayOrder: number;
}

/** Matches `apps/api` `updateAllergenSchema`: `code` is immutable, no field to change it. */
export interface AllergenUpdateInput {
  name?: string;
  icon?: string | null;
  displayOrder?: number;
  isActive?: boolean;
}

/** Matches `apps/api` `POST /allergens` (`allergens.controller.ts`). */
export function createAllergen(input: AllergenFormInput): Promise<Allergen> {
  return apiRequest<Allergen>('/allergens', { method: 'POST', body: JSON.stringify(input) });
}

/** Matches `apps/api` `PATCH /allergens/:id` (`allergens.controller.ts`). */
export function updateAllergen(id: string, input: AllergenUpdateInput): Promise<Allergen> {
  return apiRequest<Allergen>(`/allergens/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

/** Matches `apps/api` `MenuListItem` (`menu-search.repository.ts`); never carries `preparationNotes`. */
export interface MenuListItem {
  id: string;
  code: string;
  name: string;
  description: string | null;
  serviceTypeId: string;
  basePriceCents: number;
  imageUrl: string | null;
  isActive: boolean;
  deletedAt: string | null;
}

export interface MenuSearchResult {
  items: MenuListItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface MenuSearchParams {
  q: string | undefined;
  serviceTypeId: string | undefined;
  dietaryTagId: string | undefined;
  excludeAllergenId: string | undefined;
  status: MenuStatusFilter | undefined;
  page: number;
  pageSize: number;
}

/** Matches `apps/api` `StoredMenuDietaryTag`/`StoredMenuAllergen` (`menu.repository.ts`). */
export interface MenuTag {
  id: string;
  code: string;
  name: string;
}

/**
 * Matches `apps/api` `MenuDetail` (`get-menu-detail.use-case.ts`):
 * `preparationNotes` is an optional key, not `null`, when the caller lacks
 * the `KITCHEN` role or `menu.update` permission (`menus/spec.md` → "Notas
 * de preparación visibles solo para cocina"). The UI must check for the
 * key's presence, not its value.
 */
export interface MenuDetail {
  id: string;
  code: string;
  name: string;
  description: string | null;
  serviceTypeId: string;
  basePriceCents: number;
  imageUrl: string | null;
  preparationNotes?: string | null;
  isActive: boolean;
  deletedAt: string | null;
  dietaryTags: MenuTag[];
  allergens: MenuTag[];
  createdAt: string;
  updatedAt: string;
}

/** Matches `apps/api` `createMenuSchema` (`menu.dto.ts`); tags/allergens are assigned afterwards via `updateMenu` (`design.md` §6). */
export interface MenuFormInput {
  code: string;
  name: string;
  description?: string;
  serviceTypeId: string;
  basePriceCents: number;
  imageUrl?: string;
  preparationNotes?: string;
}

/** Matches `apps/api` `updateMenuSchema` (`menu.dto.ts`); every field optional, and `dietaryTagIds`/`allergenIds` are a complete replacement, never incremental. */
export interface MenuUpdateInput {
  code?: string;
  name?: string;
  description?: string | null;
  serviceTypeId?: string;
  basePriceCents?: number;
  imageUrl?: string | null;
  preparationNotes?: string | null;
  dietaryTagIds?: string[];
  allergenIds?: string[];
}

/** Matches `apps/api` `POST /menus` (`menus.controller.ts`). */
export function createMenu(input: MenuFormInput): Promise<MenuDetail> {
  return apiRequest<MenuDetail>('/menus', { method: 'POST', body: JSON.stringify(input) });
}

/** Matches `apps/api` `GET /menus/:id` (`get-menu-detail.use-case.ts`). */
export function getMenu(menuId: string): Promise<MenuDetail> {
  return apiRequest<MenuDetail>(`/menus/${menuId}`);
}

/** Matches `apps/api` `PATCH /menus/:id` (`update-menu.use-case.ts`). */
export function updateMenu(menuId: string, input: MenuUpdateInput): Promise<MenuDetail> {
  return apiRequest<MenuDetail>(`/menus/${menuId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `PATCH /menus/:id/archive` (`archive-menu.use-case.ts`). No restore endpoint exists in this change (`archive-menu.use-case.ts` doc comment). */
export function archiveMenu(menuId: string): Promise<MenuDetail> {
  return apiRequest<MenuDetail>(`/menus/${menuId}/archive`, { method: 'PATCH' });
}

/** The largest page `GET /menus` accepts; anything above is a 400. */
export const MAX_MENU_PAGE_SIZE = 100;

/** Matches `apps/api` `GET /menus` (`menu-search.dto.ts`) exactly. */
export function searchMenus(params: MenuSearchParams): Promise<MenuSearchResult> {
  const query = new URLSearchParams();

  if (params.q !== undefined && params.q !== '') {
    query.set('q', params.q);
  }
  if (params.serviceTypeId !== undefined) {
    query.set('serviceTypeId', params.serviceTypeId);
  }
  if (params.dietaryTagId !== undefined) {
    query.set('dietaryTagId', params.dietaryTagId);
  }
  if (params.excludeAllergenId !== undefined) {
    query.set('excludeAllergenId', params.excludeAllergenId);
  }
  if (params.status !== undefined) {
    query.set('status', params.status);
  }
  query.set('page', String(params.page));
  query.set('pageSize', String(params.pageSize));

  return apiRequest<MenuSearchResult>(`/menus?${query.toString()}`);
}

/**
 * Every menu matching `filters`, walking `GET /menus` past its
 * `MAX_MENU_PAGE_SIZE` cap (`fetchAllPages`) instead of the single page a
 * picker used to request — the gap that left a menu past #100 unselectable
 * anywhere (calendar entries, contract prices, order lines).
 */
export function searchAllMenus(
  filters: Omit<MenuSearchParams, 'page' | 'pageSize'>,
): Promise<MenuSearchResult> {
  return fetchAllPages<MenuListItem>(
    (page, pageSize) => searchMenus({ ...filters, page, pageSize }),
    MAX_MENU_PAGE_SIZE,
  ).then((items) => ({ items, page: 1, pageSize: MAX_MENU_PAGE_SIZE, total: items.length }));
}
